import 'react-day-picker/style.css'
import { useEffect, useState, useCallback, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { DayPicker, getDefaultClassNames } from 'react-day-picker'
// Phase 95 — le calendrier a ses PROPRES noms de mois et de jours, qui ne
// passent ni par le catalogue ni par Intl : react-day-picker les tire de son
// objet de locale. Sans cette bascule, la moitié gauche de la page resterait en
// français sous un titre anglais. `weekStartsOn={1}` reste posé en dur, donc la
// semaine commence au lundi dans les deux langues.
import { fr, enGB } from 'react-day-picker/locale'
import { useAuth } from '../auth/AuthProvider'
import { supabase } from '../lib/supabase'
import { extractCheckoutError } from '../lib/checkoutError'
import { COUNTRIES, parseStoredPhone, formatE164, validateLocalNumber } from '../lib/phone'
import type { MsgKey } from '../i18n/types'
import { useAbortableSupabaseQuery } from '../lib/useAbortableSupabaseQuery'
import {
  dispatchPackPostConfirm,
  slotBookable,
  bookableMultiHourStarts,
  type AvailabilitySlot,
} from './slotPickerHelpers'
import { selectActiveOwnHold, formatHoldExpiry, type HoldRow } from '../lib/holdBanner'
import { formatParis, parisDateKey, INTL_LOCALE } from '../lib/parisTime'
import { formatEuros } from '../lib/money'
import { useLanguage } from '../i18n/LanguageProvider'
import { pluralKey } from '../i18n/plural'
import type { Lang } from '../i18n/types'

interface SlotPickerProps {
  eventTypeSlug: string
  // Phase 89 — session length in minutes (60/120/180). Chosen UPSTREAM on the
  // dashboard radiogroup and threaded through /reserve/:Nh → ReserveSession as a
  // prop. studiocoaching-only; premiere/coaching ignore it (fixed 1h span). Anon
  // /session/checkout omits it → default 60 (operator decision: anon stays 1h).
  durationMin?: number
}

interface ReserveSlotResult {
  success: boolean
  error_code?: string
  data?: {
    booking_id: string
    held_until: string
  }
}

const defaultCN = getDefaultClassNames()

export default function SlotPicker({ eventTypeSlug, durationMin = 60 }: SlotPickerProps) {
  const navigate = useNavigate()
  const { user } = useAuth()
  // Phase 95 — `t` résout les chaînes du catalogue, `lang` n'alimente QUE les
  // deux portes de formatage (dates, prix). Aucun `Intl` n'est posé ici : le
  // fuseau du studio reste enfermé dans parisTime.ts (D-08, T-95-11).
  const { t, lang } = useLanguage()
  const localeTag = INTL_LOCALE[lang]

  // Phase 71-03 — première séance is direct-pay ONLY (10€, never pack-payable).
  // isPremiere forces the direct-payment path everywhere a pack branch would
  // otherwise appear: reserve_slot uses payment_kind='premiere', the pack-confirm
  // button (handlePackConfirm / 'btn-use-pack') is suppressed even when hasPack,
  // the promo input stays visible (so a pack-owner can apply a premiere-scoped
  // code — TRIAL-02), and the button label shows the 10€ price. The pack-credit
  // path is also blocked server-side (confirm_booking rejects the slug, 71-01) —
  // this UI suppression removes the confusing dead button.
  const isPremiere = eventTypeSlug === 'premiere-seance'

  // Phase 74-03 (COACH-01) — coaching-perso is direct-pay ONLY (80€, never
  // pack-payable) and threaded EXACTLY like isPremiere: reserve_slot uses
  // payment_kind='coaching-perso', the pack-confirm button is suppressed even
  // when hasPack, the promo input stays visible (a coaching-scoped code is
  // reachable), and the button label shows 80€. The pack-credit path is also
  // rejected server-side (confirm_booking, 74-01). Coaching REPEATS — no
  // once-per-user state (that gating lives only on the premiere page).
  const isCoachingPerso = eventTypeSlug === 'coaching-perso'

  // Phase 89 — multi-hour bookings (1h/2h/3h) are studiocoaching-ONLY. The
  // durationMin prop only takes effect here (product decision D4, 89-CONTEXT §5);
  // premiere/coaching stay 1h fixed. The server (reserve_slot) also rejects a
  // non-studiocoaching slug with duration≠60 (defence in depth).
  const isStudioCoaching = !isPremiere && !isCoachingPerso

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)
  const [slots, setSlots] = useState<AvailabilitySlot[]>([])
  const [heldUntil, setHeldUntil] = useState<string | null>(null)
  const [bookingId, setBookingId] = useState<string | null>(null)
  const [hasPack, setHasPack] = useState(false)
  // Phase 89 — numeric credit balance, needed to gate the pack button when the
  // selected duration costs more credits than the user holds (N credits for Nh).
  const [packBalance, setPackBalance] = useState(0)
  const [loading, setLoading] = useState(false)
  const [slotsLoading, setSlotsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [countdown, setCountdown] = useState<number | null>(null)
  const [isConfirming, setIsConfirming] = useState(false)
  const [toastMsg, setToastMsg] = useState<string | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)
  // Téléphone éditable en réservation (user connecté). Prérempli depuis profiles ;
  // sauvegardé au blur (facultatif, jamais bloquant). savedE164 = dernière valeur
  // persistée, pour ne réécrire que sur changement réel.
  const [phoneDial, setPhoneDial] = useState('33')
  const [phoneLocal, setPhoneLocal] = useState('')
  // Une CLÉ, plus un libellé : `validateLocalNumber` ne coud plus sa phrase
  // française (Phase 97, plan 97-07 — voir lib/phone.ts).
  const [phoneErr, setPhoneErr] = useState<MsgKey | null>(null)
  const [savedE164, setSavedE164] = useState<string | null>(null)
  const [phoneSaved, setPhoneSaved] = useState(false)
  // Phase 71 promo-in-app (operator decision 2026-06-25 — "all promo fields
  // before Stripe, no charge-then-refund"). The in-app promo input is RESTORED
  // (reverses 71-07). The code is sent as `promo_code` in the
  // create-booking-checkout body and validated PRE-payment server-side:
  // resolvePromoCode with the kind derived from the booking's slug
  // ('premiere' for première-séance, 'session' for studiocoaching). A wrong-scope
  // or invalid code returns HTTP 422 (promo_invalid_for_premiere /
  // promo_invalid_for_session / promo_new_customer_only_violated) — surfaced
  // INLINE on the promo field via setPromoError, never a generic toast, and the
  // customer is NEVER charged-then-refunded.
  const [promoCode, setPromoCode] = useState('')
  const [promoError, setPromoError] = useState<string | null>(null)

  // Phase 35 D-01/D-02 — abandoned-hold banner. A logged-in user with an active
  // own held booking gets Reprendre (resume checkout) + Libérer (release_hold).
  // Read directly from `bookings` (the ledger / get_booking_history does NOT
  // surface held rows); RLS bookings_select_own scopes the SELECT to auth.uid().
  const [holds, setHolds] = useState<HoldRow[]>([])

  // Phone prefill from profiles table
  useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    supabase
      .from('profiles')
      .select('phone')
      .eq('id', user.id)
      .maybeSingle<{ phone: string | null }>()
      .then(({ data, error }) => {
        if (cancelled || error) return
        const stored = data?.phone ?? null
        if (stored) {
          const p = parseStoredPhone(stored)
          setPhoneDial(p.dialCode)
          setPhoneLocal(p.localNumber)
        }
        setSavedE164(stored)
      })
    return () => { cancelled = true }
  }, [user?.id])

  // Persiste le numéro au blur : facultatif (vide → efface), jamais bloquant.
  // Un numéro invalide reste en state avec l'erreur affichée, non sauvegardé.
  // Découplé du bouton Payer — le webhook lit profiles bien après la redirection.
  async function handlePhoneBlur() {
    if (!user?.id) return
    const err = validateLocalNumber(phoneLocal)
    setPhoneErr(err)
    if (err) return
    const e164 = formatE164(phoneDial, phoneLocal)
    if (e164 === savedE164) return
    // Phone-only write via the SECURITY DEFINER RPC (authenticated has no direct
    // UPDATE on public.*, 20260520150000/D-05). p_full_name = null → COALESCE
    // leaves the name untouched (the prior upsert({id,phone}) never wrote it).
    const { error } = await supabase
      .rpc('update_own_profile', { p_full_name: null, p_phone: e164 })
    if (!error) {
      setSavedE164(e164)
      setPhoneSaved(true)
    }
  }

  // Pack detection — credits_balance is the source of truth (balance + expiry).
  // Migrated to useAbortableSupabaseQuery (FE-31-01): aborts the in-flight read
  // and guards setHasPack against firing after unmount.
  useAbortableSupabaseQuery<{ balance: number | null; expires_at: string | null }>(
    (signal) => {
      // No user → resolve empty (no read). Mirrors the prior `if (!user?.id) return`.
      if (!user?.id) return Promise.resolve({ data: null, error: null })
      return supabase
        .from('credits_balance')
        .select('balance, expires_at')
        .eq('user_id', user.id)
        .abortSignal(signal)
        .maybeSingle()
    },
    (data) => {
      const balance = data?.balance ?? 0
      const notExpired = !data?.expires_at || new Date(data.expires_at) > new Date()
      setHasPack(balance > 0 && notExpired)
      setPackBalance(notExpired ? balance : 0)
    },
    [user?.id],
  )

  // On mount: auto-select first date with available slots (today or next)
  useEffect(() => {
    let cancelled = false
    const findFirst = async () => {
      for (let offset = 0; offset <= 30; offset++) {
        if (cancelled) return
        const d = new Date()
        d.setDate(d.getDate() + offset)
        const { data } = await supabase.rpc('get_day_slots', { p_date: parisDateKey(d) })
        if (cancelled) return
        const available = ((data as AvailabilitySlot[]) ?? []).filter(s => slotBookable(s, isCoachingPerso))
        if (available.length > 0) {
          setSelectedDate(d)
          return
        }
      }
    }
    findFirst()
    return () => { cancelled = true }
  }, [isCoachingPerso])

  // Slot polling (fetch + 30s refresh)
  const fetchSlots = useCallback(async (date: Date) => {
    const dayStr = parisDateKey(date)
    const { data, error: fetchError } = await supabase.rpc('get_day_slots', { p_date: dayStr })
    if (fetchError) {
      setError(t('reserve.slotsLoadError'))
      return
    }
    setSlots((data as AvailabilitySlot[]) ?? [])
  }, [t])

  useEffect(() => {
    if (!selectedDate) return
    let cancelled = false
    // Loading reset on date change — intentional sync setState (codebase
    // convention, cf. AdminCodesPromo.tsx). Surfaced now that the showToast
    // hoist (FE-31-10) cleared the parse blocker that previously masked it.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSlotsLoading(true)
    setError(null)

    const run = async () => {
      if (cancelled) return
      await fetchSlots(selectedDate)
      if (!cancelled) setSlotsLoading(false)
    }
    run()

    const id = setInterval(async () => {
      if (cancelled) return
      await fetchSlots(selectedDate)
    }, 30_000)

    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [selectedDate, fetchSlots])

  // Declared BEFORE the hold-countdown effect that calls it (FE-31-10 — avoid
  // "access before declared"). useCallback gives a stable identity so it can be
  // listed in the effect deps without re-subscribing the interval each tick.
  const showToast = useCallback((msg: string) => {
    setToastMsg(msg)
    setTimeout(() => setToastMsg(null), 4000)
  }, [])

  // Phase 35 — owner-scoped held-bookings read (mount + after a release). RLS
  // bookings_select_own already restricts to auth.uid(); the explicit
  // .eq('user_id', user.id) is belt-and-suspenders + keeps the SELECT shape
  // identical to the dashboard read so the contract test covers both.
  const fetchHolds = useCallback(async () => {
    if (!user?.id) {
      setHolds([])
      return
    }
    const { data } = await supabase
      .from('bookings')
      .select('id, slot_starts_at, slot_ends_at, held_until, status')
      .eq('user_id', user.id)
      .eq('status', 'held')
      .order('held_until', { ascending: true })
    setHolds((data as HoldRow[]) ?? [])
  }, [user?.id])

  useEffect(() => {
    fetchHolds()
  }, [fetchHolds])

  const activeHold = selectActiveOwnHold(holds)

  // Reprendre — re-enter Stripe checkout for the EXISTING held booking (no new
  // reserve_slot). Mirrors handleDirectPayment's invoke + error shape. Phase 71
  // (Gemini Gate 1 FIX B): forward the current in-app promo when one is entered
  // so resuming a hold does NOT silently drop the code → full-price Stripe
  // session. The EF validates it pre-payment exactly as the direct path; a 422
  // promo error routes inline.
  // An expired hold (EF returns hold_expired) degrades to slot re-selection with
  // a FR message instead of a hard error banner (CONTEXT Discretion).
  async function handleResume(heldId: string) {
    setError(null)
    setPromoError(null)
    const trimmedPromo = promoCode.trim().toUpperCase()
    // Phase 96 (D-11) — le client envoie une LANGUE, jamais un fragment de
    // chemin : l'EF la valide contre un ensemble fermé de deux valeurs et
    // fabrique elle-même le préfixe de success_url. Un préfixe libre venu d'ici
    // serait une redirection ouverte par success_url.
    const body: { booking_id: string; lang: Lang; promo_code?: string } = { booking_id: heldId, lang }
    if (trimmedPromo) body.promo_code = trimmedPromo
    const { data, error: invokeError } = await supabase.functions.invoke('create-booking-checkout', {
      body,
    })
    if (invokeError || !data?.url) {
      const { code, messageKey, isPromo } = await extractCheckoutError(invokeError, data)
      if (code === 'hold_expired' || code === 'booking_not_held' || code === 'booking_not_found') {
        showToast(t('reserve.holdExpiredPickNew'))
        fetchHolds()
        return
      }
      if (isPromo) {
        setPromoError(t(messageKey))
        return
      }
      setError(t(messageKey))
      return
    }
    window.location.href = data.url
  }

  // Libérer — release the hold now (owner-guarded release_hold RPC) and re-fetch
  // slots so the freed slot reappears immediately.
  async function handleRelease(heldId: string) {
    setError(null)
    const { data, error: rpcError } = await supabase.rpc('release_hold', { p_booking_id: heldId })
    const ok = !rpcError && (data as { success?: boolean })?.success
    const code = (data as { error_code?: string } | null)?.error_code
    if (ok) {
      showToast(t('reserve.holdReleased'))
    } else if (!rpcError && (code === 'not_held' || code === 'slot_not_found')) {
      // Terminal, not transient — same reasoning as the dashboard row: the
      // banner can outlive the hold it describes (paid in another tab), and
      // "réessaie" would loop the user forever on a call that always refuses.
      setError(t('reserve.holdNoLongerPending'))
    } else {
      setError(t('reserve.holdReleaseFailed'))
    }
    // Resync whatever happened: a banner that outlived its hold is exactly the
    // dead end this fix exists to break, and a transport error is no reason to
    // keep showing it.
    await fetchHolds()
    if (selectedDate) fetchSlots(selectedDate)
  }

  // Hold countdown ticker
  useEffect(() => {
    if (!heldUntil) return

    const tick = () => {
      const remaining = Math.max(0, new Date(heldUntil).getTime() - Date.now())
      setCountdown(remaining)
      if (remaining === 0) {
        setHeldUntil(null)
        setBookingId(null)
        setSelectedSlot(null)
        showToast(t('reserve.slotExpired'))
      }
    }
    tick()
    const id = setInterval(tick, 1_000)
    return () => clearInterval(id)
  }, [heldUntil, showToast, t])

  async function handleChipClick(slot: AvailabilitySlot) {
    if (slot.is_taken) return
    // Pack payment requires login — direct payment is open to all.
    // B11 (Plan 32-06) : propagate the user's last-known booking email to the
    // login screen so the prefill + cooldown sync fires automatically once
    // they land there. URLSearchParams encodes `+` to `%2B` so emails like
    // `jonas.foure+e2e@gmail.com` survive the round-trip (vs inline string
    // concat which would lose the `+` as `' '` on the receiving end).
    if (!user && hasPack) {
      const params = new URLSearchParams({ next: '/#reservation' })
      const stashedEmail = typeof window !== 'undefined'
        ? window.localStorage.getItem('lastBookingEmail')
        : null
      if (stashedEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(stashedEmail)) {
        params.set('email', stashedEmail)
      }
      navigate(`/login?${params.toString()}`)
      return
    }
    setLoading(true)
    setError(null)

    const { data, error: rpcError } = await supabase.rpc('reserve_slot', {
      event_type_slug: eventTypeSlug,
      slot_starts_at: slot.slot_starts_at,
      // Phase 71-03 / 74-03 — premiere + coaching-perso are their own
      // JWT-required kinds (reserve_slot branches); never 'pack' (server rejects
      // pack-credit for these slugs).
      payment_kind: isCoachingPerso ? 'coaching-perso' : isPremiere ? 'premiere' : hasPack ? 'pack' : 'direct',
      // Phase 89 — 4th arg (DEFAULT 60 server-side). Only meaningful on
      // studiocoaching; premiere/coaching always send 60 (their span is fixed 1h).
      p_duration_min: isStudioCoaching ? durationMin : 60,
    } as Record<string, unknown>)

    setLoading(false)

    if (rpcError || !data) {
      setError(t('reserve.reserveFailed'))
      return
    }

    const result = data as ReserveSlotResult
    if (!result.success) {
      if (result.error_code === 'slot_taken' || result.error_code === 'slot_not_found') {
        // Phase 89 (Gemini Gate 1) — the FE-derived list was stale (a longer
        // span collides more often than a 1h slot). Re-fetch the day's slots
        // immediately so the lost/freed span disappears without waiting for the
        // 30s poll, in addition to the toast.
        showToast(t('reserve.slotJustTaken'))
        if (selectedDate) fetchSlots(selectedDate)
      } else if (result.error_code === 'hold_expired') {
        showToast(t('reserve.slotExpired'))
      } else {
        setError(t('reserve.reserveFailed'))
      }
      return
    }

    setSelectedSlot(slot.slot_starts_at)
    setBookingId(result.data!.booking_id)
    setHeldUntil(result.data!.held_until)
  }

  async function handlePackConfirm() {
    if (!bookingId) return
    setIsConfirming(true)

    // Phase 98 (D-01) — la langue d'ACHAT doit être sur la ligne AVANT
    // confirm_booking, jamais après. confirm_booking fait basculer le statut à
    // 'confirmed', ce qui déclenche AU COMMIT le trigger
    // booking_confirmed_dispatch (migration 20260616120000) → net.http_post vers
    // send-booking-email. Toute écriture postérieure au retour de la RPC perd la
    // course : l'email part en lisant language = NULL, retombe sur
    // profiles.language (NULL pour la quasi-totalité des comptes), donc en
    // français. D'où le `await` — un `void` ici reproduirait exactement la
    // course qu'on ferme.
    //
    // La porte d'écriture est la RPC et pas un `.update()` : `authenticated` n'a
    // AUCUN GRANT d'écriture sur `bookings`, un update direct rendrait 42501
    // (bug de la Phase 90). La garde de propriété vit DANS la RPC
    // (user_id = auth.uid(), plan 98-02), pas ici.
    //
    // T-98-08 — un échec ne doit JAMAIS faire échouer la réservation : on
    // journalise et on enchaîne. Le repli D-01 (profil, puis français) reprend
    // la main. La latence de l'aller-retour est absorbée par le spinner déjà
    // posé par setIsConfirming(true) juste au-dessus.
    const { error: langError } = await supabase.rpc('set_booking_language', {
      p_booking_id: bookingId,
      p_lang: lang,
    })
    if (langError) {
      console.warn('set_booking_language failed (booking proceeds in French)', langError)
    }

    const { data, error: rpcError } = await supabase.rpc('confirm_booking', {
      booking_id: bookingId,
      payment_kind: 'pack',
    })

    if (rpcError || !(data as { success: boolean })?.success) {
      setIsConfirming(false)
      setError(t('reserve.confirmFailed'))
      return
    }

    // Phase 69-04: fire post-confirm side effects (SwitchBot keypad + Brevo email)
    // WITHOUT awaiting. Booking is already confirmed in the DB via confirm_booking
    // RPC, which fires the Phase 69 trigger booking_confirmed_dispatch — this in turn
    // dispatches program-switchbot-pin EF + send-booking-email EF async via pg_net.
    // notify-pack-booking now serves as a redundant inline path against the same EFs
    // (idempotent via switchbot_status terminal check + email_sent_at gate); the trigger
    // path is primary, this is belt-and-suspenders. Either completes the side effects;
    // cron retry-pin-push + send-pending-booking-emails are the backstop. Errors are
    // swallowed inside dispatchPackPostConfirm (best-effort) so we intentionally drop
    // the promise — the previous `await` held the user on a spinner for 5-30s while
    // SwitchBot programmed the keypad inline, exactly the UX bottleneck Phase 69
    // eliminated for native Stripe-pay confirmations.
    void dispatchPackPostConfirm(supabase, bookingId)

    setIsConfirming(false)
    navigate(`/booking/confirmed/${bookingId}`)
  }

  async function handleDirectPayment() {
    if (!bookingId) return
    setIsConfirming(true)
    setPromoError(null)
    // B11 (Plan 32-06) : stash the user's email in localStorage so a later
    // visit to /login pre-fills it (and the Phase 28.2 cooldown sync restores
    // any GoTrue 60s window automatically). Source = authed user (anon flow
    // captures the email on Stripe's hosted page — covered by the Plan 32-07
    // success_url cascade rather than this client-side write).
    const stashEmail = user?.email
    if (typeof window !== 'undefined' && stashEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(stashEmail)) {
      window.localStorage.setItem('lastBookingEmail', stashEmail)
    }
    // Phase 71 promo-in-app — the checkout invoke carries the in-app promo when
    // supplied. create-booking-checkout validates it PRE-payment (kind derived
    // from the booking slug); a 422 promo error routes INLINE below.
    const trimmedPromo = promoCode.trim().toUpperCase()
    // Phase 96 (D-11) — cf. handleResume : une langue, jamais un chemin.
    const body: { booking_id: string; lang: Lang; promo_code?: string } = { booking_id: bookingId, lang }
    if (trimmedPromo) body.promo_code = trimmedPromo
    const { data, error: invokeError } = await supabase.functions.invoke('create-booking-checkout', {
      body,
    })
    setIsConfirming(false)

    // Surface a specific copy for each EF error code (Phase 36/37 triage fix
    // 2026-05-22). supabase-js v2 returns the error body behind
    // `invokeError.context.json()` on HTTP 4xx/5xx — NOT in `data` — so
    // extractCheckoutError reads it there (mirrors AdminPage.tsx). Promo-specific
    // codes (promo_invalid_for_premiere / _session / new_customer) route to the
    // inline promo field; everything else goes to the top-level error banner.
    // Phase 97 — l'extraction rend une CLÉ : la copie suit la langue du visiteur.
    if (invokeError || !data?.url) {
      const { messageKey, isPromo } = await extractCheckoutError(invokeError, data)
      if (isPromo) {
        setPromoError(t(messageKey))
        return
      }
      setError(t(messageKey))
      return
    }
    window.location.href = data.url
  }

  // Countdown display
  const mm = String(Math.floor((countdown ?? 0) / 60_000)).padStart(2, '0')
  const ss = String(Math.floor(((countdown ?? 0) % 60_000) / 1_000)).padStart(2, '0')
  const isWarning = (countdown ?? 0) > 0 && (countdown ?? 0) < 120_000

  const calendarClassNames = {
    root: `${defaultCN.root} bg-surface border border-border rounded-[8px] p-4`,
    day: defaultCN.day,
    day_button: `${defaultCN.day_button} transition-colors`,
    selected: defaultCN.selected,
    today: defaultCN.today,
    disabled: `${defaultCN.disabled} cursor-not-allowed`,
    chevron: `${defaultCN.chevron} fill-current`,
  }

  // Phase 89 — the duration in effect: the picked value on studiocoaching, a
  // fixed 60 everywhere else (premiere/coaching are 1h). Drives derivation,
  // the reserve_slot span, the chip end-time and the price label.
  const effectiveDuration = isStudioCoaching ? durationMin : 60
  const durationH = effectiveDuration / 60

  // Slots the user may actually book right now: at duration N, only starts whose
  // N hourly sub-slots all exist and are free (bookableMultiHourStarts), plus the
  // coaching 48h gate via slotBookable. At 60 min this is exactly the old
  // single-slot bookable set. Recomputed each render so it tracks the wall clock
  // as the 30s poll re-renders.
  const bookableSlots = bookableMultiHourStarts(slots, effectiveDuration, isCoachingPerso)
  const selectedSlotObj = selectedSlot ? slots.find(s => s.slot_starts_at === selectedSlot) : null

  // Price for the direct-pay button. studiocoaching = 25€ × N hours;
  // premiere/coaching keep their sealed fixed prices (Phase 71/74). Le CALCUL
  // est inchangé, seule son unité l'est : en centimes entiers, la forme que
  // money.ts attend (et celle que Stripe et la base stockent déjà).
  const directCents = isCoachingPerso ? 8_000 : isPremiere ? 1_000 : 2_500 * durationH
  // Phase 95 — le libellé n'assemble plus « nombre + € » : la position du
  // symbole s'inverse d'une langue à l'autre (45,00 € / €45.00), ce qu'une
  // concaténation ne peut pas savoir. formatEuros est la porte unique.
  const directLabel = t('reserve.pay', { price: formatEuros(directCents, lang) })

  return (
    <div className="w-full">
      {/* Hold-release banner (Phase 35 D-02) — abandoned own hold, logged in.
          Reprendre re-enters checkout for the same hold; Libérer frees the slot. */}
      {activeHold && (
        <div
          data-testid="slotpicker-hold-banner"
          className="mb-4 bg-surface border-l-2 border-l-accent rounded-[8px] px-4 py-3"
          role="status"
          aria-live="polite"
        >
          <p className="text-sm text-text">
            {t('reserve.pendingHold')}{' '}
            <span className="font-medium">
              {formatParis(
                activeHold.slot_starts_at,
                {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  hour: '2-digit',
                  minute: '2-digit',
                },
                localeTag,
              )}
            </span>{' '}
            <span className="text-warning">
              {t('reserve.pendingHoldExpiry', { time: formatHoldExpiry(activeHold.held_until) })}
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <button
              type="button"
              onClick={() => handleResume(activeHold.id)}
              className="bg-accent hover:bg-accent-hover text-[#1a1209] font-medium px-4 py-2 rounded-[4px] transition-all duration-200 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent cursor-pointer"
              aria-label={t('reserve.resumeAria')}
            >
              {t('reserve.resume')}
            </button>
            <button
              type="button"
              onClick={() => handleRelease(activeHold.id)}
              className="text-sm text-text-muted hover:text-text px-2 py-1 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-[2px]"
              aria-label={t('reserve.releaseAria')}
            >
              {t('reserve.release')}
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toastMsg && (
        <div
          className="mb-4 bg-surface border border-border-error rounded-[4px] px-4 py-3 text-sm text-error"
          role="alert"
          aria-live="assertive"
        >
          {toastMsg}
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          className="mb-4 bg-surface border-l-2 border-l-error rounded-[8px] px-4 py-3 text-sm text-error"
          role="alert"
        >
          {error}
        </div>
      )}

      {/* Side-by-side: calendar left, slots right */}
      <div className="flex flex-col md:flex-row gap-6 items-start justify-center">

        {/* Left — calendar + user info */}
        <div className="flex-none">
          <DayPicker
            mode="single"
            locale={lang === 'en' ? enGB : fr}
            weekStartsOn={1}
            selected={selectedDate}
            onSelect={setSelectedDate}
            disabled={[{ before: new Date() }]}
            classNames={calendarClassNames}
            style={{
              '--rdp-accent-color': '#C97D4A',
              '--rdp-background-color': 'rgba(201,125,74,0.15)',
              '--rdp-day-width': '2.75rem',
              '--rdp-day-height': '2.75rem',
              '--rdp-day_button-width': '2.75rem',
              '--rdp-day_button-height': '2.75rem',
              '--rdp-day-font-size': '1rem',
              '--rdp-weekday-font-size': '0.875rem',
              '--rdp-month_caption-font': '700 1.125rem/1.2 var(--font-display, inherit)',
              '--rdp-nav_button-width': '2.25rem',
              '--rdp-nav_button-height': '2.25rem',
            } as CSSProperties}
          />
          {user && (
            <div className="mt-3">
              <p className="text-sm text-text-muted mb-2">
                {/* `||`, not `??`. Clearing the name on /mon-compte stores '',
                    which `??` treats as a present value — the line would render
                    blank instead of falling back to the email. */}
                {(user.user_metadata?.full_name as string | undefined)?.trim() || user.email}
              </p>
              <label htmlFor="booking-phone" className="block text-xs font-medium text-text-muted mb-1">
                {t('reserve.phoneLabel')}
              </label>
              <div
                className={`flex bg-surface border rounded-[4px] overflow-hidden max-w-[280px] ${phoneErr ? 'border-error' : 'border-border'}`}
              >
                <select
                  value={phoneDial}
                  onChange={(e) => { setPhoneDial(e.target.value); setPhoneErr(null); setPhoneSaved(false) }}
                  aria-label={t('reserve.phoneDialAria')}
                  className="bg-surface text-text text-sm px-2 py-2 border-r border-border focus:outline-none cursor-pointer shrink-0"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.dial}>{c.flag} +{c.dial}</option>
                  ))}
                </select>
                <input
                  id="booking-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  value={phoneLocal}
                  onChange={(e) => { setPhoneLocal(e.target.value.replace(/\D/g, '')); setPhoneErr(null); setPhoneSaved(false) }}
                  onBlur={handlePhoneBlur}
                  placeholder="6 12 34 56 78"
                  aria-invalid={!!phoneErr}
                  className="flex-1 bg-transparent px-2 py-2 text-text text-sm placeholder:text-text-muted/50 focus:outline-none min-w-0"
                />
              </div>
              {phoneErr ? (
                <p role="alert" className="text-error text-xs mt-1">{t(phoneErr)}</p>
              ) : phoneSaved ? (
                <p className="text-success text-xs mt-1">{t('reserve.phoneSaved')}</p>
              ) : (
                <p className="text-text-muted text-xs mt-1">{t('reserve.phoneHint')}</p>
              )}
            </div>
          )}
        </div>

        {/* Right — slot list (only when date selected) */}
        {selectedDate ? (
          <div className="flex-none w-full md:w-[200px]">
            <div className="flex items-baseline justify-between mb-3">
              {/* Le jour affiché vient de la MÊME porte que le jour interrogé
                  (parisDateKey ci-dessus) : sans cela, un visiteur dont le
                  fuseau a déjà changé de date lirait un titre décalé d'un jour
                  par rapport aux créneaux listés dessous. */}
              <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-text">
                {formatParis(selectedDate, { weekday: 'long', day: 'numeric', month: 'long' }, localeTag)}
              </h2>
            </div>

            {!heldUntil && bookableSlots.length > 0 && (
              <p className="text-xs text-text-muted mb-3">{t('reserve.holdNotice')}</p>
            )}

            {slotsLoading && (
              <div className="flex items-center gap-2 text-sm text-text-muted">
                <div className="w-4 h-4 border-2 border-text-muted/30 border-t-accent rounded-full animate-spin" />
                {t('reserve.slotsLoading')}
              </div>
            )}

            {!slotsLoading && bookableSlots.length === 0 && (
              <div className="bg-surface border border-border rounded-[8px] p-6">
                <h3 className="font-semibold mb-2">{t('reserve.noSlotsTitle')}</h3>
                <p className="text-text-muted text-sm">
                  {isCoachingPerso ? t('reserve.noSlotsCoaching') : t('reserve.noSlotsDay')}
                </p>
              </div>
            )}

            {!slotsLoading && bookableSlots.length > 0 && (
              <div
                className="overflow-y-auto max-h-[340px] flex flex-col gap-1 pr-1"
                role="group"
                aria-label={t('reserve.slotsGroupAria', {
                  date: formatParis(
                    selectedDate,
                    { day: '2-digit', month: '2-digit', year: 'numeric' },
                    localeTag,
                  ),
                })}
              >
                {bookableSlots.map((slot) => {
                  const isSelected = selectedSlot === slot.slot_starts_at
                  // Phase 91 — always render the studio (Europe/Paris) wall-clock,
                  // never the visitor's browser zone (formatParis forces the tz).
                  const startTime = formatParis(
                    slot.slot_starts_at,
                    { hour: '2-digit', minute: '2-digit' },
                    localeTag,
                  )
                  // Phase 89 — the chip end reflects the selected N-hour span,
                  // not the underlying 1h slot's end.
                  const endTime = formatParis(
                    new Date(new Date(slot.slot_starts_at).getTime() + effectiveDuration * 60_000),
                    { hour: '2-digit', minute: '2-digit' },
                    localeTag,
                  )

                  const chipClass = 'w-full px-4 py-2.5 rounded-[4px] text-sm text-left flex items-center justify-between transition-colors duration-200 ' + (
                    isSelected
                      ? 'bg-accent-hover text-bg ring-2 ring-accent ring-offset-1 ring-offset-bg font-semibold'
                      : 'bg-surface border border-border hover:border-accent hover:bg-accent/10 text-text cursor-pointer'
                  )

                  return (
                    <button
                      key={`${slot.event_type_slug}-${slot.slot_starts_at}`}
                      aria-pressed={isSelected}
                      data-testid="slot-chip"
                      data-slot-starts-at={slot.slot_starts_at}
                      className={chipClass}
                      onClick={() => !loading && handleChipClick(slot)}
                      disabled={loading}
                    >
                      <span className="font-medium">{startTime}</span>
                      <span className="text-xs opacity-60">→ {endTime}</span>
                    </button>
                  )
                })}
              </div>
            )}

            {/* Loading state for reserve_slot */}
            {loading && (
              <div className="flex items-center gap-2 mt-3 text-sm text-text-muted">
                <div className="w-4 h-4 border-2 border-text-muted/30 border-t-accent rounded-full animate-spin" />
                {t('reserve.reserving')}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 hidden md:flex items-center justify-center text-text-muted text-sm">
            {t('reserve.pickDate')}
          </div>
        )}
      </div>

      {/* Payment section — below the two columns, shown after hold confirmed */}
      {heldUntil && bookingId && countdown !== null && countdown > 0 && (
        <div className="mt-6 border-t border-border pt-6">
          {/* Selected slot summary */}
          {selectedSlotObj && (
            <p className="text-sm text-text-muted mb-4">
              {t('reserve.selectedSlot')}{' '}
              <span className="text-text font-medium">
                {formatParis(selectedSlotObj.slot_starts_at, { weekday: 'long', day: 'numeric', month: 'long' }, localeTag)}{' '}
                {formatParis(selectedSlotObj.slot_starts_at, { hour: '2-digit', minute: '2-digit' }, localeTag)}
                {' – '}
                {formatParis(new Date(new Date(selectedSlotObj.slot_starts_at).getTime() + effectiveDuration * 60_000), { hour: '2-digit', minute: '2-digit' }, localeTag)}
              </span>
            </p>
          )}

          {/* Countdown */}
          <p
            className={`text-sm mb-4 ${isWarning ? 'text-error font-normal animate-pulse' : 'text-text-muted'}`}
            aria-live={isWarning ? 'assertive' : 'polite'}
          >
            {t('reserve.holdCountdown', { time: `${mm}:${ss}` })}
          </p>

          {/* Promo code (Phase 71 promo-in-app — RESTORED). Sent as promo_code
              in the create-booking-checkout body and validated PRE-payment with
              the kind derived from the booking slug ('premiere' / 'session').
              ALWAYS visible: the direct-payment button is offered on EVERY path
              (incl. when a pack-owner chooses to pay a session at the hourly
              rate instead of spending a credit), so the code must be enterable
              there too. It is simply ignored if the user redeems a pack credit
              (handlePackConfirm bills nothing). Earlier the field was hidden
              when hasPack — that wrongly blocked a pack-owner from applying a
              code on a direct-pay session (operator-reported 2026-06-26). */}
          {(
            <div className="mb-4 max-w-sm">
              <label htmlFor="promo-code" className="text-xs text-text-muted mb-1 block">
                {t('reserve.promoLabel')}
              </label>
              <input
                id="promo-code"
                type="text"
                value={promoCode}
                onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(null) }}
                placeholder="SUMMER2026"
                autoComplete="off"
                spellCheck={false}
                aria-invalid={promoError !== null}
                aria-describedby={promoError ? 'promo-code-error' : undefined}
                disabled={isConfirming}
                className={`w-full bg-surface border rounded-[4px] px-4 py-2.5 text-sm text-text placeholder:text-text-muted/50 uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-bg disabled:opacity-60 ${promoError ? 'border-error' : 'border-border focus:border-accent'}`}
              />
              {promoError && (
                <p id="promo-code-error" data-testid="promo-code-error" role="alert" className="text-error text-xs mt-1">
                  {promoError}
                </p>
              )}
            </div>
          )}

          {/* Payment buttons. Phase 71-03: premiere forces the SINGLE
              direct-payment branch (no pack-confirm / 'btn-use-pack') even when
              hasPack — premiere is never pack-payable. */}
          <div className="flex flex-col sm:flex-row gap-3 max-w-sm">
            {hasPack && !isPremiere && !isCoachingPerso ? (
              <>
                <button
                  onClick={handlePackConfirm}
                  disabled={isConfirming || packBalance < durationH}
                  title={
                    packBalance < durationH
                      ? t(pluralKey('reserve.packCreditsNeeded', durationH, lang), {
                          count: durationH,
                          balance: packBalance,
                        })
                      : undefined
                  }
                  className="bg-accent hover:bg-accent-hover text-bg font-semibold px-6 py-3 rounded-[4px] flex-1 transition-all duration-200 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg inline-flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="btn-use-pack"
                >
                  {isConfirming ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-bg/30 border-t-bg rounded-full animate-spin" />
                      {t('reserve.confirming')}
                    </span>
                  ) : (
                    // Forme plurielle choisie par ICU (plan 95-09) et non plus
                    // par un ternaire : ici {count} vaut 1, 2 ou 3 — jamais 0 —
                    // donc les deux langues tombaient d'accord par chance. Le
                    // helper enlève la chance de l'équation.
                    t(pluralKey('reserve.usePackCredit', durationH, lang), {
                      count: durationH,
                    })
                  )}
                </button>
                <button
                  onClick={handleDirectPayment}
                  disabled={isConfirming}
                  className="border border-border hover:border-accent text-text px-6 py-3 rounded-[4px] flex-1 transition-colors duration-200 inline-flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="btn-pay-direct"
                >
                  {directLabel}
                </button>
              </>
            ) : (
              <button
                onClick={handleDirectPayment}
                disabled={isConfirming}
                className="bg-accent hover:bg-accent-hover text-[#1a1209] font-medium px-6 py-3 rounded-[4px] transition-all duration-200 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg inline-flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="btn-proceed-payment"
              >
                {isConfirming ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-bg/30 border-t-[#1a1209] rounded-full animate-spin" />
                    {t('reserve.redirecting')}
                  </span>
                ) : directLabel}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
